import { DateTime } from 'luxon';
import React from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Emoji from 'react-emoji-render';
import { WithTranslation, withTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import { PayPalButton } from 'react-paypal-button-v2';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { alertActions, transactionActions, userActions } from '../../actions';
import HeadOverride from '../../components/general/HeadOverride';
import ForcePaymentShortkeys from '../../components/shortkey/ForcePaymentShortkeys';
import { uiHelpers } from '../../helpers';
import {
  getEffectivePromoCode,
  getPaypalClientId,
  getStripePriceId,
  isMonthRestricted,
  PLAN_KOL_V1_PROMO,
  planRestriction,
  PRICE_CONFIG_DEFAULT,
  PRICE_CONFIG_SANDBOX,
  totalAmount,
} from '../../helpers/price.helper';
import {
  ComponentWithRouterProps,
  withRouter,
} from '../../helpers/router.helper';
import { WithFeatures, withFeatures } from '../../hooks/useFeatureFlags';
import {
  SandboxBanner,
  WithSandboxPayment,
  withSandboxPayment,
} from '../../hooks/useSandboxPayment';
import { RootState } from '../../reducers';
import { transactionService } from '../../services';
import { ReduxProps } from '../../types';
import PaymentSelection, { MonthOptions } from './PaymentSelection';
import PlanCards from './PlanCards';
import StripeCheckoutButton from './StripeCheckoutButton';
import SubscriptionsTable from './SubscriptionsTable';

interface State {
  month: MonthOptions;
  incrementKeyUsedToForceUpdateSubscriptionDetail: number;
  freeTrialAvailable: boolean;
  showPaypal: boolean;
}

type Props = ReduxProps<typeof mapStateToProps> &
  WithTranslation &
  ComponentWithRouterProps &
  WithSandboxPayment &
  WithFeatures;

class PaymentPage extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      month: 6,
      incrementKeyUsedToForceUpdateSubscriptionDetail: 0,
      freeTrialAvailable: false,
      showPaypal: false,
    };

    this.refresh = this.refresh.bind(this);
  }

  componentDidMount() {
    this.refresh();

    if (window.location.href.includes('success=true')) {
      this.autoRefreshAfterPayment();
    }
  }

  componentDidUpdate() {
    const { stripeCoupon } = this.props;
    if (isMonthRestricted(this.state.month, stripeCoupon)) {
      this.setState({
        month: Number.parseInt(
          planRestriction(stripeCoupon)[0].replace('m', '')
        ) as 1 | 6 | 12,
      });
    }
  }

  refresh() {
    const { dispatch } = this.props;
    dispatch(userActions.refresh());
    this.setState({
      incrementKeyUsedToForceUpdateSubscriptionDetail:
        this.state.incrementKeyUsedToForceUpdateSubscriptionDetail + 1,
    });
    transactionService
      .freeTrialAvailable()
      .then(({ freeTrialAvailable }) => this.setState({ freeTrialAvailable }))
      .catch(error => {
        if (error.name !== 'UserTokenInvalid') {
          throw error;
        }
      });
  }

  priceReady() {
    const { PRICE_PER_MONTH, DISCOUNT_FOR_12_MONTH, loadingPrices } =
      this.props;
    return PRICE_PER_MONTH > 0 && DISCOUNT_FOR_12_MONTH < 1 && !loadingPrices;
  }

  showPurchaseOptions = () => {
    const { hasMembership, expireAt } = this.props.user;
    const { paymentViewEnabled } = this.props;

    return (
      !hasMembership ||
      (expireAt && expireAt < Date.now() / 1000) ||
      paymentViewEnabled
    );
  };

  renderStripeButton = () => {
    const { usingSandboxPayment, promo } = this.props;
    const { month, freeTrialAvailable } = this.state;

    const priceId = getStripePriceId(promo, month, usingSandboxPayment);

    return (
      <StripeCheckoutButton
        isSandbox={usingSandboxPayment}
        priceId={priceId}
        hasTrial={freeTrialAvailable}
        promo={this.usingPromoCode()}
        isStripeNativePromo={this.isStripeNativePromo()}
      />
    );
  };

  renderMorePaymentOptionsButton = () => {
    const { t } = this.props;
    const { showPaypal } = this.state;
    return (
      <Button
        style={{ width: '100%', padding: '8px 0', marginBottom: '24px' }}
        variant="outline-primary"
        onClick={() => {
          this.setState({ showPaypal: !showPaypal });
        }}
      >
        {t('other-payment-methods')}{' '}
        {showPaypal ? <FaAngleUp /> : <FaAngleDown />}
      </Button>
    );
  };

  usingPromoCode = () =>
    getEffectivePromoCode(this.props.promo, this.props.stripeCoupon);

  isStripeNativePromo = () =>
    !!(this.props.stripeCoupon && this.usingPromoCode());

  isPaypalAvailable = () =>
    !this.isStripeNativePromo() && this.state.month !== 1;

  locale = () => {
    const { i18n } = this.props;
    return `${i18n.language}_HK`;
  };

  renderPurchaseOption = () => {
    const {
      user: { id },
      PRICE_PER_MONTH,
      DISCOUNT_FOR_12_MONTH,
      loadingPrices,
      promo,
      stripeCoupon,
      dispatch,
      t,
      usingSandboxPayment,
    } = this.props;
    const { month, freeTrialAvailable, showPaypal } = this.state;
    return (
      <div>
        {usingSandboxPayment && <SandboxBanner />}
        <PaymentSelection
          freeTrialAvailable={freeTrialAvailable}
          month={month}
          setMonth={_m => this.setState({ month: _m })}
        />
        {this.priceReady() && this.renderStripeButton()}
        {this.priceReady() &&
          this.isPaypalAvailable() &&
          this.renderMorePaymentOptionsButton()}
        {this.priceReady() && this.isPaypalAvailable() && (
          <CSSTransition
            in={showPaypal}
            timeout={300}
            classNames="animated-height-paypal-button"
            unmountOnExit
          >
            <PayPalButton
              options={{
                vault: true,
                clientId: getPaypalClientId(usingSandboxPayment),
                currency: 'HKD',
                locale: this.locale(),
              }}
              createSubscription={(_, actions) => {
                const priceConfig = usingSandboxPayment
                  ? PRICE_CONFIG_SANDBOX
                  : PRICE_CONFIG_DEFAULT;
                const plans = promo ? PLAN_KOL_V1_PROMO : priceConfig;
                return actions.subscription.create({
                  plan_id:
                    month === 6
                      ? freeTrialAvailable
                        ? plans.plan6m_trial
                        : plans.plan6m
                      : freeTrialAvailable
                        ? plans.plan1y_trial
                        : plans.plan1y,
                  custom_id: JSON.stringify({
                    userId: id,
                    promo: promo ? promo.code : undefined,
                  }),
                });
              }}
              onApprove={(data, actions) => {
                return actions.subscription.get().then(details => {
                  const { subscriptionID } = data;

                  dispatch(
                    transactionActions.add(
                      totalAmount(
                        PRICE_PER_MONTH,
                        DISCOUNT_FOR_12_MONTH,
                        loadingPrices,
                        month,
                        promo,
                        stripeCoupon
                      ),
                      JSON.stringify(details),
                      'paypal-subscription',
                      subscriptionID,
                      promo ? promo.code : undefined
                    )
                  );

                  this.autoRefreshAfterPayment();
                });
              }}
            />
          </CSSTransition>
        )}
        <small style={{ fontSize: '0.7rem', fontWeight: 300 }}>
          <i>* {t('recurring-payment')}</i>
        </small>
      </div>
    );
  };

  autoRefreshAfterPayment = () => {
    const { dispatch, t, nagivate } = this.props;
    window.setTimeout(() => {
      this.refresh();
    }, 60_000);

    dispatch(alertActions.success(t('payment-success')));
    nagivate('/payment/thankyou');
  };

  render() {
    const { expireAt } = this.props.user;
    const { t, features } = this.props;
    const {
      month,
      freeTrialAvailable,
      incrementKeyUsedToForceUpdateSubscriptionDetail,
    } = this.state;
    return (
      <Container>
        <HeadOverride>Membership & Payment</HeadOverride>
        <Row>
          <Col
            md={{
              span: this.showPurchaseOptions() ? 8 : 12,
              offset: 0,
            }}
          >
            <Row>
              <Col md={8}>
                <SubscriptionsTable
                  key={incrementKeyUsedToForceUpdateSubscriptionDetail}
                />
              </Col>
            </Row>
            <Card>
              <Card.Header>
                {t('Membership')}{' '}
                <MdRefresh
                  style={{ cursor: 'pointer' }}
                  onClick={this.refresh}
                />
              </Card.Header>
              <Card.Body>
                <div>
                  {expireAt && (
                    <div>
                      {t('membership-remain')}&nbsp;
                      {DateTime.fromMillis(expireAt * 1000)
                        .diffNow('days')
                        .days.toFixed(0)}{' '}
                      {t('days')} (
                      {uiHelpers.formatDateLocalFromTimestamp(expireAt)})
                      <Emoji text="🥂" />
                    </div>
                  )}
                  {this.showPurchaseOptions() && (
                    <div>
                      <small>{t('almost-join-us')}</small>
                      <PlanCards
                        month={month}
                        freeTrialDisabled={!freeTrialAvailable}
                        hideSwitch
                      />
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          {/*
           * We use HTML "display: none" to hide it since
           * paypal SDK has some issue when us mount/unmount
           * paypal buttons dynamically.
           */}
          <Col
            style={{
              marginTop: '3rem',
              display: this.showPurchaseOptions() ? 'unset' : 'none',
            }}
            md={4}
          >
            {this.renderPurchaseOption()}
          </Col>
        </Row>
        {features.includes('FORCE_PAYMENT_ENABLED') && (
          <ForcePaymentShortkeys />
        )}
      </Container>
    );
  }
}

function mapStateToProps(state: RootState) {
  const {
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loading: loadingPrices,
  } = state.transaction.prices;
  const {
    promo,
    stripeCoupon,
    code: requestedPromo,
    loading: loadingPromo,
  } = state.transaction.promo;
  const { user } = state.user;
  const { paymentViewEnabled } = state.admin.payment;
  return {
    paymentViewEnabled,
    user,
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loadingPrices,
    promo,
    stripeCoupon,
    requestedPromo,
    loadingPromo,
  };
}

export default withRouter(
  withTranslation()(
    connect(mapStateToProps)(withSandboxPayment(withFeatures(PaymentPage)))
  )
);
